/* eslint-disable @next/next/no-img-element */
'use client'
import { RequestOtpAction, VerifyOtpAction } from '@/actions'
import { ButtonRoundStyle, Form } from '@/components'
import { CALLBACK_URL_KEY } from '@/constants'
import { OTPCountDown } from '@/features/auth'
import { setRef } from '@/features/ref/utils/setRef'
import { useAccountStore, useCartStore, useCompareStore } from '@/store'
import { DEFAULT_LOGIN_REDIRECT } from '@/utils/routes'
import yup from '@/utils/yup-global'
import { otpVerifySchema } from '@/validations'
import { yupResolver } from '@hookform/resolvers/yup'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Stack } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
type Props = {
  onBack: () => void
  onSuccess: () => void
  phone: string
}

export const OTPForm = ({ onBack, onSuccess, phone }: Props) => {
  const [isPending, startTransition] = useTransition()
  const [otpKey, setOtpKey] = useState(0) // Key để force re-render OTP component
  const t = useTranslations('UserProfile')
  const { setUser } = useAccountStore()
  const { mergeCart } = useCartStore()
  const { assignToUser } = useCompareStore()
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get(CALLBACK_URL_KEY)

  const method = useForm<yup.InferType<typeof otpVerifySchema>>({
    resolver: yupResolver(otpVerifySchema),
    defaultValues: {
      phone: phone,
      otp: '',
    },
  })

  const { handleSubmit, setValue } = method

  // Xác thực OTP và đăng nhập
  const onSubmit = async (values: yup.InferType<typeof otpVerifySchema>) => {
    startTransition(async () => {
      try {
        const result = await VerifyOtpAction(values, callbackUrl)

        if (result?.status === 'error') {
          toast.error(result?.message || t('Xác thực OTP thất bại'))
          return
        }

        // Cập nhật thông tin user và merge data
        await setUser(result?.data ?? {})
        await mergeCart()
        await assignToUser()

        const redirectUrl = callbackUrl ?? DEFAULT_LOGIN_REDIRECT
        // const refValue = result?.data?.increment_id
        // const redirectUrlWithRef = setRef(refValue, redirectUrl)
        window.location.href = redirectUrl
        // router.push(redirectUrl)

        // Gọi callback success nếu cần
        onSuccess()
      } catch (error) {
        console.error('Verify OTP error:', error)
        toast.error(t('Có lỗi xảy ra trong quá trình xác thực OTP'))
      }
    })
  }

  // Handle OTP input complete
  const handleOtpComplete = (otpValue: string) => {
    setValue('otp', otpValue)
    handleSubmit(onSubmit)()
  }

  // Handle resend OTP
  const handleResendOTP = async () => {
    try {
      const result = await RequestOtpAction({ phone })

      if (result?.status === 'error') {
        toast.error(result?.message || t('Gửi lại mã OTP thất bại'))
        return
      }

      // Reset OTP input bằng cách force re-render component
      setValue('otp', '')
      setOtpKey((prev) => prev + 1)
    } catch (error) {
      console.error('Resend OTP error:', error)
      toast.error(t('Có lỗi xảy ra khi gửi lại mã OTP'))
    }
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)} methods={method}>
      <OTPCountDown
        key={otpKey} // Force re-render khi resend OTP
        onSubmit={handleOtpComplete}
        onResendOTP={handleResendOTP}
        align="center"
        subtitle={t.rich(
          'Vui lòng nhập đoạn mã gồm 6 chữ số vừa gửi để xác minh tài khoản vừa nhập là của bạn',
          {
            goal: 'Zalo ' + phone,
            bold: (chunks) => <strong>{chunks}</strong>,
          }
        )}
      />
      <Stack spacing={1} mt={2}>
        <ButtonRoundStyle variant="contained" type="submit" disabled={isPending} loading={isPending}>
          {t('Xác minh tài khoản')}
        </ButtonRoundStyle>

        <ButtonRoundStyle variant="outlined" disabled={isPending} onClick={onBack}>
          <ArrowBackIcon /> {t('Quay về')}
        </ButtonRoundStyle>
      </Stack>
    </Form>
  )
}
